import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { esocialReferenceService, ESocialReferenceItem } from '../../../../../services/esocialReferenceService';

/**
 * Só a Tabela 24 avisa "código não reconhecido": as demais tabelas de referência
 * ainda não foram semeadas, e o aviso acusaria como inválido todo valor já gravado.
 */
const TABELA_AGENTES_NOCIVOS = 24;

interface Props {
  tableNumber: number;
  value: string;
  onChange: (code: string, description: string) => void;
  label: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
}

const ESocialReferenceAutocomplete: React.FC<Props> = ({
  tableNumber, value, onChange, label, required, error, helperText, disabled, size
}) => {
  const [options, setOptions] = useState<ESocialReferenceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selected, setSelected] = useState<ESocialReferenceItem | null>(null);
  const [unrecognizedCode, setUnrecognizedCode] = useState<string | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const displayedCode = useRef('');

  /**
   * O que a pessoa digita não muda `value` — só a escolha de uma opção muda, e aí o código
   * já está exibido. Por isso este efeito só reage ao código que vem de fora, e ignora o
   * que ele mesmo acabou de exibir.
   */
  useEffect(() => {
    if (value === displayedCode.current) return;
    displayedCode.current = value;

    if (!value) {
      setSelected(null);
      setInputValue('');
      setUnrecognizedCode(null);
      return;
    }

    let current = true;
    esocialReferenceService.lookup(tableNumber, [value]).then(items => {
      if (!current) return;
      if (items.length > 0) {
        setSelected(items[0]!);
        setInputValue(`${items[0]!.code} - ${items[0]!.description}`);
        setUnrecognizedCode(null);
      } else {
        setSelected(null);
        setInputValue(value);
        setUnrecognizedCode(tableNumber === TABELA_AGENTES_NOCIVOS ? value : null);
      }
    }).catch(() => {});
    return () => { current = false; };
  }, [value, tableNumber]);

  const handleSearch = (query: string) => {
    if (query.length < 1) { setOptions([]); return; }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await esocialReferenceService.search(tableNumber, query);
        setOptions(results);
      } catch { setOptions([]); }
      finally { setLoading(false); }
    }, 300);
  };

  return (
    <Autocomplete
      value={selected}
      onChange={(_, val) => {
        displayedCode.current = val?.code ?? '';
        setSelected(val);
        setUnrecognizedCode(null);
        onChange(val?.code ?? '', val?.description ?? '');
      }}
      inputValue={inputValue}
      onInputChange={(_, v) => { setInputValue(v); handleSearch(v); }}
      options={options}
      loading={loading}
      disabled={disabled}
      size={size}
      fullWidth
      getOptionLabel={(o) => `${o.code} - ${o.description}`}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      filterOptions={(x) => x}
      noOptionsText={inputValue.length < 1 ? 'Digite para buscar' : 'Nenhum resultado'}
      renderInput={(params) => (
        <TextField {...params} label={label} required={required} error={error}
          helperText={unrecognizedCode
            ? `Código "${unrecognizedCode}" não reconhecido na Tabela 24. Selecione o agente correto.`
            : helperText}
          slotProps={{
            formHelperText: unrecognizedCode ? { sx: { color: 'warning.main' } } : undefined,
            input: {
              ...params.InputProps,
              endAdornment: (
                <>{loading && <CircularProgress color="inherit" size={20} />}{params.InputProps.endAdornment}</>
              ),
            }
          }}
        />
      )}
    />
  );
};

export default ESocialReferenceAutocomplete;
